import { createRootRoute, createRoute, lazyRouteComponent, redirect } from "@tanstack/react-router";
import { AbsencesPage } from "@/modules/absences/pages/AbsencesPage";
import { DeleteAccountPage } from "@/modules/accountDeletion/pages/DeleteAccountPage";
import { AttendanceDaysPage } from "@/modules/attendance/pages/AttendanceDaysPage";
import { AuditPage } from "@/modules/audit/pages/AuditPage";
import { LoginPage } from "@/modules/auth/pages/LoginPage";
import { DashboardPage } from "@/modules/dashboard/pages/DashboardPage";
import { DepartmentDetailPage } from "@/modules/departments/pages/DepartmentDetailPage";
import { DepartmentsPage } from "@/modules/departments/pages/DepartmentsPage";
import { DevicesPage } from "@/modules/devices/pages/DevicesPage";
import { EmployeeDetailPage } from "@/modules/employees/pages/EmployeeDetailPage";
import { EmployeesPage } from "@/modules/employees/pages/EmployeesPage";
import { JobsPage } from "@/modules/jobs/pages/JobsPage";
import { LeadFormBuilderPage } from "@/modules/leadForms/pages/LeadFormBuilderPage";
import { LeadFormsPage } from "@/modules/leadForms/pages/LeadFormsPage";
import { LeadDetailPage } from "@/modules/leads/pages/LeadDetailPage";
import { LeadsPage } from "@/modules/leads/pages/LeadsPage";
import { NotificationChannelsPage } from "@/modules/notifications/pages/NotificationChannelsPage";
import { NotificationsPage } from "@/modules/notifications/pages/NotificationsPage";
import { PositionsPage } from "@/modules/positions/pages/PositionsPage";
import { ReportDetailPage } from "@/modules/reports/pages/ReportDetailPage";
import { ReportsPage } from "@/modules/reports/pages/ReportsPage";
import { RetentionPage } from "@/modules/retention/pages/RetentionPage";
import { RolesPage } from "@/modules/roles/pages/RolesPage";
import { HolidaysPage } from "@/modules/schedules/pages/HolidaysPage";
import { SchedulesPage } from "@/modules/schedules/pages/SchedulesPage";
import { ShiftInstancesPage } from "@/modules/schedules/pages/ShiftInstancesPage";
import { SettingsPage } from "@/modules/settings/pages/SettingsPage";
import { TasksPage } from "@/modules/tasks/pages/TasksPage";
import { UsersPage } from "@/modules/users/pages/UsersPage";
import { AuthLayout } from "@/shared/components/layouts/AuthLayout";
import { MainLayout } from "@/shared/components/layouts/MainLayout";
import { NotFoundRoute } from "./NotFoundRoute";
import { RootRoute } from "./RootRoute";

/**
 * `staticData` — TanStack Router'ning kutubxona darajasidagi kengaytirish
 * nuqtasi (`Register` interfeysi bilan bir xil naqsh). Standart holatda bo'sh
 * (`{}`), shuning uchun bu yerda `requirePermission` maydonini deklaratsiya
 * birlashtirish (declaration merging) orqali qo'shamiz — aks holda na
 * `createRoute({ staticData })` ga yozish, na uni keyinroq o'qish (`route.
 * options.staticData` yoki `match.staticData` orqali) tipланmaydi.
 */
declare module "@tanstack/react-router" {
	interface StaticDataRouteOption {
		/** Talab qilinadigan permission, `"resource:action"` ko'rinishida. */
		requirePermission?: string;
	}
}

/**
 * Code-based route daraxti (spec D3 — fayl asosidagi generatsiya emas).
 *
 * Permission talabi `staticData.requirePermission` da e'lon qilinadi va
 * `MainLayout` ichidagi `ProtectedRoute` uni o'qiydi. Rolga qarab tekshirish
 * YO'Q — RBAC dinamik, rollar DB'da (template qoidasi).
 */
const rootRoute = createRootRoute({
	component: RootRoute,
	// ⚠️ **Avval bu yerda `<Navigate to="/" replace />` turardi va u har
	// qanday noma'lum URL da brauzer tabini CRASH qilardi** — sabab
	// `NotFoundRoute.tsx` da batafsil. Bu yerdagi komponent navigatsiya
	// QILMAYDI; agar kimdir uni yana avtomatik yo'naltirishga o'zgartirsa,
	// sikl qaytadi.
	notFoundComponent: NotFoundRoute,
});

const authLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "auth-layout",
	component: AuthLayout,
});

const loginRoute = createRoute({
	getParentRoute: () => authLayoutRoute,
	path: "/login",
	component: LoginPage,
});

/**
 * `/delete-account` — Google Play «Data deletion» talabi uchun OCHIQ sahifa.
 *
 * ⚠️ **`rootRoute` ostida to'g'ridan-to'g'ri — layout marshrutlaridan
 * TASHQARIDA.** `authLayoutRoute` kirgan foydalanuvchini `/dashboard` ga
 * otib yuboradi, `mainLayoutRoute` esa `ProtectedRoute` bilan o'ralgan;
 * ikkalasi ham do'kon tekshiruvchisiga sahifani ko'rsatmasdi. Kirmagan
 * tashrifchi uchun kirish `RootRoute` ning `PUBLIC_PATHS` ro'yxati bilan
 * ochiladi — ikkalasi BIRGA o'zgaradi.
 *
 * ⚠️ `staticData.requirePermission` ATAYLAB yo'q: sahifa RBAC dan
 * tashqarida, chunki uni ochadigan odamda hisob umuman bo'lmasligi mumkin.
 */
const deleteAccountRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/delete-account",
	component: DeleteAccountPage,
});

const mainLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "main-layout",
	component: MainLayout,
});

const indexRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/",
	beforeLoad: () => {
		throw redirect({ to: "/dashboard" });
	},
});

const dashboardRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/dashboard",
	component: DashboardPage,
});

const usersRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/users",
	component: UsersPage,
	staticData: { requirePermission: "users:read" },
});

const rolesRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/roles",
	component: RolesPage,
	staticData: { requirePermission: "roles:read" },
});

/**
 * Rollar — bitta ekran (spec `2026-09-14-rollar-bitta-ekran-design.md`):
 * uch marshrut BITTA `RolesPage` ni ko'rsatadi, rejim URL'dan olinadi.
 * Yassi (`getParentRoute: () => mainLayoutRoute`), TO'LIQ yo'l bilan va har
 * biri O'Z `staticData`si bilan (`tasksRoute` naqshi).
 *
 * ⚠️ `/roles/$roleId` — `roles:read`, `roles:update` EMAS: huquqi faqat
 * o'qishga yetadigan foydalanuvchi rolni ko'radi, panel esa «Saqlash»siz.
 * `/roles/new` statik segment — `$roleId` dan ustun turadi.
 */
const roleCreateRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/roles/new",
	component: RolesPage,
	staticData: { requirePermission: "roles:create" },
});

const roleDetailRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/roles/$roleId",
	component: RolesPage,
	staticData: { requirePermission: "roles:read" },
});

/** Eski tahrirlash manzili (xatcho'plar) — endi panel `/roles/$roleId` da. */
const roleEditRedirectRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/roles/$roleId/edit",
	beforeLoad: ({ params }) => {
		throw redirect({ to: "/roles/$roleId", params: { roleId: params.roleId } });
	},
});

const devicesRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/devices",
	component: DevicesPage,
	staticData: { requirePermission: "devices:read" },
});

const departmentsRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/departments",
	component: DepartmentsPage,
	staticData: { requirePermission: "departments:read" },
});

/**
 * ⚠️ Yassi bola marshrut — `employeeDetailRoute` naqshi: `getParentRoute`
 * `mainLayoutRoute`, yo'l TO'LIQ (`/departments/$id`), `staticData`
 * O'ZINIKI (`/departments` ota sifatida MOS KELMAYDI).
 */
const departmentDetailRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/departments/$id",
	component: DepartmentDetailPage,
	staticData: { requirePermission: "departments:read" },
});

const positionsRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/positions",
	component: PositionsPage,
	staticData: { requirePermission: "positions:read" },
});

const employeesRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/employees",
	component: EmployeesPage,
	staticData: { requirePermission: "employees:read" },
});

/**
 * ⚠️ Yassi bola marshrut — `leadDetailRoute` naqshi: `getParentRoute`
 * `mainLayoutRoute`, yo'l esa TO'LIQ (`/employees/$id`).
 *
 * ⚠️ `staticData` O'ZINIKI bo'lishi SHART: `MainLayout`
 * `matches.map(...).at(-1)` bilan faqat MOS KELGAN marshrutning
 * `staticData` sini o'qiydi, ya'ni `/employees` ota sifatida
 * MOS KELMAYDI va usiz sahifa qorovulsiz ochilardi.
 */
const employeeDetailRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/employees/$id",
	component: EmployeeDetailPage,
	staticData: { requirePermission: "employees:read" },
});

/**
 * ⚠️ Xarita marshrutlari LAZY — `/zones`, `/attendance/live-map`,
 * `/tasks/new`, `/tasks/$taskId`, `/leads/map` (pastda). Leaflet + geoman +
 * pmtiles birgalikda +154 KB gzip (1c spec §10.1 da o'lchangan; 09.09
 * build'ida geoman yolg'iz 72 KB), xarita esa faqat shu sahifalarda kerak.
 * Statik import ularni har kirgan foydalanuvchiga yuklardi.
 *
 * Ikkinchi argument — eksport nomi (`ZonesPage`), chunki modulda default
 * eksport yo'q.
 */
const zonesRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/zones",
	component: lazyRouteComponent(() => import("@/modules/zones/pages/ZonesPage"), "ZonesPage"),
	staticData: { requirePermission: "zones:read" },
});

/*
 * ⚠️ Bu to'rtta marshrut STATIK import qiladi — `lazyRouteComponent` faqat
 * xarita marshrutlarida (yuqoridagi izoh). Sabab: og'irlik xarita
 * kutubxonasida, bu sahifalar esa faqat mavjud antd komponentlaridan iborat
 * va alohida bo'lakka ajratilsa, foyda o'rniga qo'shimcha so'rov qo'shardi.
 */
const schedulesRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/schedules",
	component: SchedulesPage,
	staticData: { requirePermission: "schedules:read" },
});

// `holidays` alohida RBAC resursi EMAS — `schedules` doirasining qismi
// (`holidays.routes.ts` da `requirePermission("schedules", ...)`).
const holidaysRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/holidays",
	component: HolidaysPage,
	staticData: { requirePermission: "schedules:read" },
});

// `shift-instances` ham shu resursda; qatorlar filtri backend'da
// `resolveScope` orqali bajariladi.
const shiftInstancesRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/shift-instances",
	component: ShiftInstancesPage,
	staticData: { requirePermission: "schedules:read" },
});

// `attendance` — qatorlar bo'yicha filtrlanadigan resurs; qator filtri
// backend'da `resolveScope` orqali bajariladi.
const attendanceRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/attendance",
	component: AttendanceDaysPage,
	staticData: { requirePermission: "attendance:read" },
});

/**
 * ⚠️ `/attendance` va `/attendance/dashboard` — ikkalasi ham STATIK segment,
 * ya'ni TanStack Router uchun to'qnashuv YO'Q (dinamik segment bo'lganda
 * aniq segment ustun bo'lardi).
 *
 * ⚠️ **«Bugungi holat» sahifasi O'CHIRILDI, marshrut YO'NALTIRADI.**
 * U `/dashboard` dagi kartalarning qism to'plami edi va ikki menyu bandi
 * bitta savolga javob berardi. Marshrutning o'zi qoladi: uni xatcho'p
 * qilgan foydalanuvchi 404 emas, boshqaruv panelini ko'radi.
 *
 * ⚠️ Backend `GET /attendance/dashboard` endpointi QOLADI — `useDashboard`
 * aynan shuni chaqiradi.
 *
 * ⚠️ `staticData` OLIB TASHLANDI: `beforeLoad` qorovuldan OLDIN ishlaydi,
 * ya'ni `attendance:read` ruxsati yo'q foydalanuvchi ham baribir
 * `/dashboard` ga tushadi — bu to'g'ri, chunki u sahifa ruxsat talab
 * qilmaydi.
 */
const attendanceDashboardRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/attendance/dashboard",
	beforeLoad: () => {
		throw redirect({ to: "/dashboard" });
	},
});

/**
 * Jonli xarita (Q3) — polling, SSE emas.
 *
 * ⚠️ **`lazyRouteComponent` MAJBURIY** — sahifa Leaflet import qiladi
 * (+154 KB gzip) va u faqat shu marshrutda kerak. `/zones` bilan bir xil
 * sabab; qorovul — `modules/attendance/attendance-bundle.test.ts`.
 *
 * ⚠️ `/attendance`, `/attendance/dashboard` va `/attendance/live-map` —
 * uchalasi ham STATIK segment, ya'ni to'qnashuv YO'Q.
 */
const attendanceLiveMapRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/attendance/live-map",
	component: lazyRouteComponent(
		() => import("@/modules/attendance/pages/LiveMapPage"),
		"LiveMapPage"
	),
	staticData: { requirePermission: "attendance:read" },
});

const settingsRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/settings",
	component: SettingsPage,
	staticData: { requirePermission: "settings:read" },
});

/**
 * ⚠️ `staticData.requirePermission` ATAYLAB yo'q — dashboard bilan bir xil
 * holat: bu foydalanuvchining O'Z ma'lumoti va backend uni `recipient_id`
 * bo'yicha filtrlaydi (KONTRAKT NUQSONI N7). Ruxsat talab qilinsa, roli yo'q
 * yangi xodim o'z xabarlarini ko'ra olmasdi.
 */
const notificationsRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/notifications",
	component: NotificationsPage,
});

// Kanal sozlamalari `notification_channels` sozlamasini yozadi, ya'ni talab
// qilinadigan ruxsat ham `settings` resursida.
const notificationChannelsRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/notification-channels",
	component: NotificationChannelsPage,
	staticData: { requirePermission: "settings:read" },
});

/*
 * ── 3-bosqich · poydevor e'lon qilgan route'lar ──
 *
 * ⚠️ **Bloklar SHU FAYLGA TEGMAYDI** (kontrakt §9 + §11). Route'lar,
 * yo'llar, `staticData` va komponent nomlari shu yerda MUZLATILGAN;
 * blok faqat O'Z modulidagi sahifa faylini qayta yozadi va eksport
 * nomini saqlaydi. Bu qaror `routes/index.ts` bilan bir xil sababdan:
 * to'rt blok parallel worktree'da ishlaydi, umumiy faylning import
 * bloki esa to'rttala tarmoqda to'qnashardi.
 *
 * ⚠️ **`/tasks/new`, `/tasks/$taskId` va `/leads/map` Leaflet
 * ishlatadi** — ular DARHOL `lazyRouteComponent` bilan e'lon qilinadi
 * (CLAUDE.md: xarita marshrutlari lazy, Leaflet + geoman +154 KB gzip,
 * `leaflet.css` `index.css` ga import QILINMAYDI). Bloklar ularni
 * keyin «lazy ga o'tkazmaydi» — bu ish shu yerda BAJARILGAN. Aynan shu
 * sababdan `TaskCreatePage`, `TaskDetailPage` va `LeadsMapPage`
 * yuqoridagi statik import ro'yxatida YO'Q.
 *
 * ⚠️ `/leads/map` va `/leads/$leadId` — birinchisi STATIK segment,
 * ikkinchisi dinamik. TanStack Router statik segmentni ustun qo'yadi,
 * ya'ni to'qnashuv YO'Q (`/attendance` va `/attendance/dashboard`
 * bilan bir xil holat).
 */
const tasksRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/tasks",
	component: TasksPage,
	staticData: { requirePermission: "tasks:read" },
});

const taskCreateRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/tasks/new",
	component: lazyRouteComponent(
		() => import("@/modules/tasks/pages/TaskCreatePage"),
		"TaskCreatePage"
	),
	staticData: { requirePermission: "tasks:create" },
});

const taskDetailRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/tasks/$taskId",
	component: lazyRouteComponent(
		() => import("@/modules/tasks/pages/TaskDetailPage"),
		"TaskDetailPage"
	),
	staticData: { requirePermission: "tasks:read" },
});

const leadFormsRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/lead-forms",
	component: LeadFormsPage,
	staticData: { requirePermission: "lead-forms:read" },
});

const leadFormDetailRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/lead-forms/$formId",
	component: LeadFormBuilderPage,
	staticData: { requirePermission: "lead-forms:read" },
});

const leadsRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/leads",
	component: LeadsPage,
	staticData: { requirePermission: "leads:read" },
});

const leadsMapRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/leads/map",
	component: lazyRouteComponent(() => import("@/modules/leads/pages/LeadsMapPage"), "LeadsMapPage"),
	staticData: { requirePermission: "leads:read" },
});

const leadDetailRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/leads/$leadId",
	component: LeadDetailPage,
	staticData: { requirePermission: "leads:read" },
});

const absencesRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/absences",
	component: AbsencesPage,
	staticData: { requirePermission: "absences:read" },
});

/*
 * ── 4-bosqich · poydevor e'lon qilgan to'rt ASOSIY marshrut ──
 *
 * ⚠️ **Bloklar (H · J · K) bu blokka TEGMAYDI** (kontrakt §0). Yo'llar,
 * `staticData` va komponent nomlari shu yerda MUZLATILGAN; blok faqat
 * O'Z modulidagi sahifa faylini qayta yozadi va eksport nomini saqlaydi.
 * Sabab 3-bosqich bilan bir xil: to'rt blok parallel worktree'da
 * ishlaydi va umumiy faylning import bloki to'rttala tarmoqda
 * to'qnashardi.
 *
 * ⚠️ **BOLA marshrut qo'shish — uch qadamli, aynan shu tartibda.**
 * (Faqat H bloki qo'shadi: `/reports/$kind`; I · J · K bu faylga
 * umuman tegmaydi.) Ilgari bu yerda «faqat faylning OXIRIGA» deb
 * yozilgan edi — u BAJARIB BO'LMAYDI: `export const routeTree`
 * faylning oxirgi ifodasi, ya'ni undan KEYIN e'lon qilingan `const`
 * `addChildren([...])` ro'yxatida `ReferenceError: Cannot access
 * '...' before initialization` beradi (TDZ) va butun daraxt moduli
 * yiqilib **oq ekran** chiqadi; ro'yxatga qo'shilmasa esa marshrut
 * jimgina O'LIK qoladi.
 *
 *   1. Marshrutni SHU bloknining oxirida, `export const routeTree`
 *      DAN OLDIN e'lon qiling.
 *   2. Uni `mainLayoutRoute.addChildren([...])` ro'yxatiga qo'shing —
 *      bu muzlatilgan mintaqadagi YAGONA ruxsat etilgan tahrir
 *      (bitta qator, `// ── 4-bosqich ──` blokining oxirida).
 *   3. Bolaga O'Z `staticData.requirePermission` ini bering. Bu
 *      MAJBURIY: `MainLayout` ning `LayoutContent` i
 *      `matches.map(...).filter(Boolean).at(-1)` bilan o'qiydi, ya'ni
 *      yassi bola (`getParentRoute: () => mainLayoutRoute`) `/reports`
 *      ni umuman MOS KELTIRMAYDI va `requirePermission` `undefined`
 *      bo'lib qoladi — `ProtectedRoute` esa uni qorovulsiz o'tkazadi.
 *
 * Naqsh mavjud: `leadDetailRoute` (`/leads/$leadId`) va
 * `leadFormDetailRoute` — ikkalasi ham yassi va ikkalasi ham o'z
 * `staticData` siga ega.
 *
 * ⚠️ **`lazyRouteComponent` KERAK EMAS** — bu sahifalarda Leaflet yo'q
 * va ular faqat mavjud antd komponentlaridan iborat (`/zones` yagona
 * lazy marshrut bo'lib qoladi).
 */
const reportsRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/reports",
	component: ReportsPage,
	staticData: { requirePermission: "reports:read" },
});

/**
 * ── H bloki qo'shgan YAGONA bola marshrut (kontrakt §0) ──
 *
 * ⚠️ E'lon `export const routeTree` DAN OLDIN — undan keyin yozilgan
 * `const` ni `addChildren` ro'yxatida ishlatish TDZ (`ReferenceError`)
 * beradi va butun daraxt yiqiladi.
 *
 * ⚠️ Yassi bola: `getParentRoute: () => mainLayoutRoute` va TO'LIQ yo'l
 * (`/reports/$kind`) — `leadDetailRoute` naqshi. `staticData` O'ZINIKI,
 * chunki `ProtectedRoute` faqat MOS KELGAN marshrutning `staticData`
 * sini o'qiydi: usiz bola qorovulsiz ochilardi.
 */
const reportDetailRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/reports/$kind",
	component: ReportDetailPage,
	staticData: { requirePermission: "reports:read" },
});

// ⚠️ `audit`, `jobs` va `retention` — FAQAT `admin` da (kontrakt §5.7);
// `manager` roliga bu ruxsatlar ATAYLAB berilmagan, ya'ni menyu bandi
// ham, marshrut ham unga ko'rinmaydi.
const auditRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/audit",
	component: AuditPage,
	staticData: { requirePermission: "audit:read" },
});

const jobsRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/jobs",
	component: JobsPage,
	staticData: { requirePermission: "jobs:read" },
});

const retentionRoute = createRoute({
	getParentRoute: () => mainLayoutRoute,
	path: "/retention",
	component: RetentionPage,
	staticData: { requirePermission: "retention:read" },
});

export const routeTree = rootRoute.addChildren([
	authLayoutRoute.addChildren([loginRoute]),
	deleteAccountRoute,
	mainLayoutRoute.addChildren([
		indexRoute,
		dashboardRoute,
		usersRoute,
		rolesRoute,
		roleCreateRoute,
		roleDetailRoute,
		roleEditRedirectRoute,
		devicesRoute,
		departmentsRoute,
		departmentDetailRoute,
		positionsRoute,
		employeesRoute,
		employeeDetailRoute,
		zonesRoute,
		schedulesRoute,
		holidaysRoute,
		shiftInstancesRoute,
		attendanceRoute,
		attendanceDashboardRoute,
		attendanceLiveMapRoute,
		settingsRoute,
		notificationsRoute,
		notificationChannelsRoute,
		// ── 3-bosqich ──
		tasksRoute,
		taskCreateRoute,
		taskDetailRoute,
		leadFormsRoute,
		leadFormDetailRoute,
		leadsRoute,
		leadsMapRoute,
		leadDetailRoute,
		absencesRoute,
		// ── 4-bosqich ──
		reportsRoute,
		reportDetailRoute,
		auditRoute,
		jobsRoute,
		retentionRoute,
	]),
]);
